import { useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  NavLink,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCopy,
  IconEdit,
  IconDownload,
  IconPlus,
  IconSearch,
  IconUpload,
  IconTrash,
  IconX,
} from "@/components/icons";
import { KnowledgeArticleContent } from "@/components/knowledge/KnowledgeArticleContent";
import { KnowledgeArticleAttachments } from "@/components/knowledge/KnowledgeArticleAttachments";
import { EmptyState, PageCenterLoader } from "@/components/ui";
import { formatDateTimeDot } from "@/lib/ticket-display";
import type {
  ProjectRow,
  SupportKnowledgeArticleRow,
  SupportKnowledgeMaterialType,
  SupportKnowledgeSearchRow,
  SupportKnowledgeTagRow,
  SupportKnowledgeTagRuleRow,
  SupportKnowledgeVariableDefinitionRow,
} from "@/types/api";
import {
  MATERIAL_TYPE_COLORS,
  articleCopyText,
  articleCountLabel,
  articlePreviewText,
  authorLabel,
  countByType,
  isKnowledgeGeneralTab,
  type ProjectArticlesBundle,
} from "@/components/knowledge/knowledge-base-shared";

type MaterialTypeOption = { key: string; label: string };

export const KNOWLEDGE_VARIABLES_SECTION = "__kb_variables__";
export const KNOWLEDGE_TAGS_SECTION = "__kb_tags__";
export const KNOWLEDGE_TAG_RULES_SECTION = "__kb_tag_rules__";

export type KnowledgeBaseWorkspaceProps = {
  generalArticles: SupportKnowledgeArticleRow[];
  bundles: ProjectArticlesBundle[];
  materialTypes: MaterialTypeOption[];
  searchResults: SupportKnowledgeSearchRow[] | null;
  debouncedSearch: string;
  loading: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  selectedProjectId: string | null;
  selectedTypeKey: string;
  onSelectType: (key: string) => void;
  selectedArticleId: string | null;
  onSelectArticle: (article: SupportKnowledgeArticleRow | null) => void;
  viewArticle: SupportKnowledgeArticleRow | null;
  viewArticleLoading: boolean;
  onOpenCreate: (
    project: ProjectRow | null,
    materialType?: SupportKnowledgeMaterialType,
  ) => void;
  onOpenEdit: (
    project: ProjectRow | null,
    article: SupportKnowledgeArticleRow,
  ) => void;
  onDelete: (
    project: ProjectRow | null,
    article: SupportKnowledgeArticleRow,
  ) => void;
  onExportArticle: (
    project: ProjectRow | null,
    article: SupportKnowledgeArticleRow,
  ) => void;
  onImportArticle: (
    project: ProjectRow | null,
    article: SupportKnowledgeArticleRow,
    file: File,
  ) => void;
  projectById: Map<string, ProjectRow>;
  getVariableDefinitions?: (
    article: SupportKnowledgeArticleRow,
  ) => Record<string, string>;
  variables: SupportKnowledgeVariableDefinitionRow[];
  tags: SupportKnowledgeTagRow[];
  tagRules: SupportKnowledgeTagRuleRow[];
  onOpenCreateVariable: (projectId?: string | null) => void;
  onOpenEditVariable: (row: SupportKnowledgeVariableDefinitionRow) => void;
  onDeleteVariable: (row: SupportKnowledgeVariableDefinitionRow) => void;
  onOpenCreateTag: (projectId?: string | null) => void;
  onOpenEditTag: (row: SupportKnowledgeTagRow) => void;
  onDeleteTag: (row: SupportKnowledgeTagRow) => void;
  onOpenCreateTagRule: (projectId?: string | null) => void;
  onOpenEditTagRule: (row: SupportKnowledgeTagRuleRow) => void;
  onDeleteTagRule: (row: SupportKnowledgeTagRuleRow) => void;
};

function ArticleRow({
  article,
  selected,
  searchRow,
  canEdit,
  canDelete,
  onSelect,
  onEdit,
  onDelete,
  onExport,
  onImport,
  compact,
  projectName,
}: {
  article: SupportKnowledgeArticleRow;
  selected: boolean;
  searchRow?: SupportKnowledgeSearchRow;
  canEdit: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onExport: () => void;
  onImport: (file: File) => void;
  compact?: boolean;
  projectName?: string | null;
}) {
  const importFileRef = useRef<HTMLInputElement | null>(null);
  const plainPreview = articlePreviewText(article);
  const copyText = articleCopyText(article);
  const snippet =
    searchRow?.match_snippet ||
    plainPreview.slice(0, compact ? 100 : 140) +
      (plainPreview.length > (compact ? 100 : 140) ? "…" : "");

  return (
    <UnstyledButton
      onClick={onSelect}
      w="100%"
      p="xs"
      style={{
        borderRadius: "var(--mantine-radius-md)",
        background: selected ? "var(--mantine-color-brand-light)" : undefined,
        border: selected
          ? "1px solid var(--mantine-color-brand-outline)"
          : "1px solid transparent",
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={selected ? 600 : 500} size="sm" lineClamp={2}>
            {article.title}
          </Text>
          {snippet ? (
            <Text size="xs" c="dimmed" mt={2} lineClamp={compact ? 1 : 2}>
              {snippet}
            </Text>
          ) : null}
          <Group gap={4} mt={4} wrap="wrap">
            {projectName ? (
              <Badge size="xs" variant="outline" color="gray">
                {projectName}
              </Badge>
            ) : null}
            {searchRow && searchRow.relevance_score > 0 ? (
              <Badge size="xs" variant="outline" color="green">
                {Math.round(searchRow.relevance_score * 100)}%
              </Badge>
            ) : null}
            {!article.is_active ? (
              <Badge size="xs" color="gray">
                Неактивен
              </Badge>
            ) : null}
            {(article.attachments?.length ?? 0) > 0 ? (
              <Badge size="xs" variant="dot" color="blue">
                {article.attachments!.length} файл
              </Badge>
            ) : null}
          </Group>
        </Box>
        <Group gap={2} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Выгрузить">
            <ActionIcon variant="subtle" size="sm" onClick={onExport}>
              <IconDownload size={14} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <CopyButton value={copyText || plainPreview || article.title}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? "Скопировано" : "Копировать"}>
                <ActionIcon variant="subtle" size="sm" onClick={copy}>
                  <IconCopy size={14} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
          {canEdit ? (
            <Tooltip label="Редактировать">
              <ActionIcon variant="subtle" size="sm" onClick={onEdit}>
                <IconEdit size={14} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          {canDelete ? (
            <Tooltip label="Удалить">
              <ActionIcon
                variant="subtle"
                size="sm"
                color="red"
                onClick={onDelete}
              >
                <IconTrash size={14} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          {canEdit ? (
            <Tooltip label="Загрузить">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => importFileRef.current?.click()}
              >
                <IconUpload size={14} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      </Group>
      <input
        ref={importFileRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          onImport(file);
          e.target.value = "";
        }}
      />
    </UnstyledButton>
  );
}

function DetailPanel({
  article,
  loading,
  projectById,
  onExport,
  onImport,
  canEdit,
  onEdit,
  onClose,
  relatedArticles,
  onArticleClick,
  variableDefinitions,
}: {
  article: SupportKnowledgeArticleRow;
  loading: boolean;
  projectById: Map<string, ProjectRow>;
  onExport: () => void;
  onImport: (file: File) => void;
  canEdit: boolean;
  onEdit: () => void;
  onClose?: () => void;
  relatedArticles?: SupportKnowledgeArticleRow[];
  onArticleClick?: (article: SupportKnowledgeArticleRow) => void;
  variableDefinitions?: Record<string, string>;
}) {
  const importFileRef = useRef<HTMLInputElement | null>(null);
  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs" mb={6} wrap="wrap">
            <Badge
              variant="light"
              color={MATERIAL_TYPE_COLORS[article.material_type] || "gray"}
            >
              {article.material_type_label}
            </Badge>
            <Text size="xs" c="dimmed">
              {article.project_id
                ? projectById.get(article.project_id)?.name || "Проект"
                : "Общие"}
            </Text>
          </Group>
          <Text fw={600} size="lg" style={{ wordBreak: "break-word" }}>
            {article.title}
          </Text>
        </Box>
        {onClose ? (
          <ActionIcon variant="subtle" onClick={onClose} aria-label="Закрыть">
            <IconX size={18} stroke={1.5} />
          </ActionIcon>
        ) : null}
      </Group>

      {(article.tags || []).length > 0 ? (
        <Group gap={4}>
          {(article.tags ?? []).map((tag) => (
            <Badge key={tag} size="sm" variant="dot">
              {tag}
            </Badge>
          ))}
        </Group>
      ) : null}

      <ScrollArea
        style={{ flex: 1, minHeight: 0 }}
        offsetScrollbars
        type="auto"
      >
        {loading ? (
          <PageCenterLoader />
        ) : (
          <KnowledgeArticleContent
            content={article.content ?? ""}
            projectId={article.project_id}
            contentFormat={article.content_format ?? "html"}
            relatedArticles={relatedArticles}
            onArticleClick={onArticleClick}
            variableDefinitions={variableDefinitions}
          />
        )}
      </ScrollArea>

      {(article.attachments?.length ?? 0) > 0 ? (
        <KnowledgeArticleAttachments
          articleId={article.id}
          attachments={article.attachments || []}
          pendingFiles={[]}
          canEdit={false}
          onAttachmentsChange={() => {}}
          onPendingChange={() => {}}
        />
      ) : null}

      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text size="xs" c="dimmed">
          Обновлено: {formatDateTimeDot(article.updated_at)} ·{" "}
          {authorLabel(article)}
        </Text>
        <Group gap="xs">
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconDownload size={14} />}
            onClick={onExport}
          >
            Выгрузить
          </Button>
          <CopyButton value={articleCopyText(article)}>
            {({ copied, copy }) => (
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconCopy size={14} />}
                onClick={copy}
              >
                {copied ? "Скопировано" : "Копировать"}
              </Button>
            )}
          </CopyButton>
          {canEdit ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconUpload size={14} />}
              onClick={() => importFileRef.current?.click()}
            >
              Загрузить
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconEdit size={14} />}
              onClick={onEdit}
            >
              Редактировать
            </Button>
          ) : null}
        </Group>
      </Group>
      <input
        ref={importFileRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          onImport(file);
          e.target.value = "";
        }}
      />
    </Stack>
  );
}

export function KnowledgeBaseWorkspace({
  generalArticles,
  bundles,
  materialTypes,
  searchResults,
  debouncedSearch,
  loading,
  canAdd,
  canEdit,
  canDelete,
  selectedProjectId,
  selectedTypeKey,
  onSelectType,
  selectedArticleId,
  onSelectArticle,
  viewArticle,
  viewArticleLoading,
  onOpenCreate,
  onOpenEdit,
  onDelete,
  onExportArticle,
  onImportArticle,
  projectById,
  getVariableDefinitions,
  variables,
  tags,
  tagRules,
  onOpenCreateVariable,
  onOpenEditVariable,
  onDeleteVariable,
  onOpenCreateTag,
  onOpenEditTag,
  onDeleteTag,
  onOpenCreateTagRule,
  onOpenEditTagRule,
  onDeleteTagRule,
}: KnowledgeBaseWorkspaceProps) {
  const theme = useMantineTheme();
  const isWide = useMediaQuery(`(min-width: 1100px)`) ?? false;
  const isNavColumn = useMediaQuery(`(min-width: 768px)`) ?? false;
  const [listFilter, setListFilter] = useState("");

  const isSearchMode = Boolean(debouncedSearch && searchResults);

  const isGeneralTab = isKnowledgeGeneralTab(selectedProjectId);

  const activeContext = useMemo(() => {
    if (isKnowledgeGeneralTab(selectedProjectId)) {
      return {
        articles: generalArticles,
        project: null as ProjectRow | null,
        isGeneral: true,
      };
    }
    const bundle = bundles.find((b) => b.project.id === selectedProjectId);
    if (!bundle) return null;
    return {
      articles: bundle.articles,
      project: bundle.project,
      isGeneral: false,
    };
  }, [generalArticles, bundles, selectedProjectId]);

  const typeCounts = useMemo(
    () => countByType(activeContext?.articles ?? []),
    [activeContext],
  );

  const projectVariables = useMemo(() => {
    if (!activeContext) return [];
    return variables.filter((row) =>
      activeContext.project
        ? !row.project_id || row.project_id === activeContext.project.id
        : !row.project_id,
    );
  }, [activeContext, variables]);
  const projectTags = useMemo(() => {
    if (!activeContext) return [];
    return tags.filter((row) =>
      activeContext.project
        ? !row.project_id || row.project_id === activeContext.project.id
        : !row.project_id,
    );
  }, [activeContext, tags]);
  const projectTagRules = useMemo(() => {
    if (!activeContext) return [];
    return tagRules.filter((row) =>
      activeContext.project
        ? !row.project_id || row.project_id === activeContext.project.id
        : !row.project_id,
    );
  }, [activeContext, tagRules]);

  const isDictionarySection =
    selectedTypeKey === KNOWLEDGE_VARIABLES_SECTION ||
    selectedTypeKey === KNOWLEDGE_TAGS_SECTION ||
    selectedTypeKey === KNOWLEDGE_TAG_RULES_SECTION;

  const browseArticles = useMemo(() => {
    if (!activeContext) return [];
    if (isDictionarySection) return [];
    let arts = [...activeContext.articles];
    if (selectedTypeKey !== "all") {
      arts = arts.filter(
        (a) => (a.material_type || "other") === selectedTypeKey,
      );
    }
    const q = listFilter.trim().toLowerCase();
    if (q) {
      arts = arts.filter((a) => {
        const preview = articlePreviewText(a).toLowerCase();
        return a.title.toLowerCase().includes(q) || preview.includes(q);
      });
    }
    arts.sort((a, b) => a.title.localeCompare(b.title, "ru"));
    return arts;
  }, [activeContext, selectedTypeKey, listFilter, isDictionarySection]);

  const searchArticles = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort(
      (a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0),
    );
  }, [searchResults]);

  const listArticles = isSearchMode ? searchArticles : browseArticles;
  const allKnownArticles = useMemo(
    () => [...generalArticles, ...bundles.flatMap((bundle) => bundle.articles)],
    [generalArticles, bundles],
  );

  const panelShell = {
    border: "1px solid var(--mantine-color-gray-3)",
    borderRadius: "var(--mantine-radius-md)",
    background: "var(--mantine-color-body)",
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  };

  const renderDictionaryList = () => {
    if (!activeContext) {
      return (
        <EmptyState
          title="Выберите раздел"
          description="Переменные и метки ведутся в общих настройках и проектах"
        />
      );
    }
    const scopeLabel = activeContext.project
      ? activeContext.project.name || activeContext.project.slug
      : "Общие";
    const scopeProjectId = activeContext.project?.id;

    if (selectedTypeKey === KNOWLEDGE_VARIABLES_SECTION) {
      return (
        <Stack gap="sm" p="sm" h="100%" style={{ minHeight: 0 }}>
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="xs"
          >
            <Box style={{ minWidth: 0 }}>
              <Text fw={600} size="sm">
                Переменные · {scopeLabel}
              </Text>
              <Text size="xs" c="dimmed">
                {projectVariables.length}
              </Text>
            </Box>
            {canAdd ? (
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconPlus size={14} stroke={1.5} />}
                onClick={() => onOpenCreateVariable(scopeProjectId)}
              >
                Добавить
              </Button>
            ) : null}
          </Group>
          <ScrollArea
            style={{ flex: 1, minHeight: 0 }}
            offsetScrollbars
            type="auto"
          >
            <Table striped highlightOnHover verticalSpacing="sm" miw={720}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ключ</Table.Th>
                  <Table.Th>Определение</Table.Th>
                  <Table.Th>Раздел</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {projectVariables.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" ff="monospace" fw={600}>
                        [{row.key}]
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={3}>
                        {row.definition}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {row.project_id ? row.project_name || "Проект" : "Общие"}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={row.is_active ? "green" : "gray"}
                        variant="light"
                      >
                        {row.is_active ? "активна" : "выключена"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        <Button
                          size="compact-sm"
                          variant="light"
                          onClick={() => onOpenEditVariable(row)}
                        >
                          Изменить
                        </Button>
                        {canDelete ? (
                          <Button
                            size="compact-sm"
                            color="red"
                            variant="light"
                            onClick={() => onDeleteVariable(row)}
                          >
                            Удалить
                          </Button>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {projectVariables.length === 0 ? (
              <EmptyState
                title="Нет переменных"
                description="Добавьте переменные в этот раздел"
              />
            ) : null}
          </ScrollArea>
        </Stack>
      );
    }

    if (selectedTypeKey === KNOWLEDGE_TAGS_SECTION) {
      return (
        <Stack gap="sm" p="sm" h="100%" style={{ minHeight: 0 }}>
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="xs"
          >
            <Box style={{ minWidth: 0 }}>
              <Text fw={600} size="sm">
                Метки · {scopeLabel}
              </Text>
              <Text size="xs" c="dimmed">
                {projectTags.length}
              </Text>
            </Box>
            {canAdd ? (
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconPlus size={14} stroke={1.5} />}
                onClick={() => onOpenCreateTag(scopeProjectId)}
              >
                Добавить
              </Button>
            ) : null}
          </Group>
          <ScrollArea
            style={{ flex: 1, minHeight: 0 }}
            offsetScrollbars
            type="auto"
          >
            <Table striped highlightOnHover verticalSpacing="sm" miw={760}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Метка</Table.Th>
                  <Table.Th>Описание</Table.Th>
                  <Table.Th>Раздел</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {projectTags.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 6,
                            background: row.color || "#B0E0E6",
                            border: "1px solid var(--mantine-color-gray-3)",
                            flex: "0 0 auto",
                          }}
                        />
                        <Text size="sm" fw={600}>
                          {row.name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={3}>
                        {row.description || "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {row.project_id ? row.project_name || "Проект" : "Общие"}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={row.is_active ? "green" : "gray"}
                        variant="light"
                      >
                        {row.is_active ? "активна" : "выключена"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        <Button
                          size="compact-sm"
                          variant="light"
                          onClick={() => onOpenEditTag(row)}
                        >
                          Изменить
                        </Button>
                        {canDelete ? (
                          <Button
                            size="compact-sm"
                            color="red"
                            variant="light"
                            onClick={() => onDeleteTag(row)}
                          >
                            Удалить
                          </Button>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {projectTags.length === 0 ? (
              <EmptyState
                title="Нет меток"
                description="Добавьте метки в этот раздел"
              />
            ) : null}
          </ScrollArea>
        </Stack>
      );
    }

    return (
      <Stack gap="sm" p="sm" h="100%" style={{ minHeight: 0 }}>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Box style={{ minWidth: 0 }}>
            <Text fw={600} size="sm">
              Правила меток · {scopeLabel}
            </Text>
            <Text size="xs" c="dimmed">
              {projectTagRules.length}
            </Text>
          </Box>
          {canAdd ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconPlus size={14} stroke={1.5} />}
              disabled={projectTags.length === 0}
              onClick={() => onOpenCreateTagRule(scopeProjectId)}
            >
              Добавить
            </Button>
          ) : null}
        </Group>
        <ScrollArea
          style={{ flex: 1, minHeight: 0 }}
          offsetScrollbars
          type="auto"
        >
          <Table striped highlightOnHover verticalSpacing="sm" miw={880}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Правило</Table.Th>
                <Table.Th>Метка</Table.Th>
                <Table.Th>Ключевые слова</Table.Th>
                <Table.Th>Раздел</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {projectTagRules.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {row.name || "Без названия"}
                    </Text>
                    {row.description ? (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {row.description}
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>{row.tag_name || "—"}</Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={3}>
                      {row.keywords || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {row.project_id ? row.project_name || "Проект" : "Общие"}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={row.is_active ? "green" : "gray"}
                      variant="light"
                    >
                      {row.is_active ? "активно" : "выключено"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Button
                        size="compact-sm"
                        variant="light"
                        onClick={() => onOpenEditTagRule(row)}
                      >
                        Изменить
                      </Button>
                      {canDelete ? (
                        <Button
                          size="compact-sm"
                          color="red"
                          variant="light"
                          onClick={() => onDeleteTagRule(row)}
                        >
                          Удалить
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {projectTagRules.length === 0 ? (
            <EmptyState
              title="Нет правил"
              description="Добавьте правила для меток этого раздела"
            />
          ) : null}
        </ScrollArea>
      </Stack>
    );
  };

  const renderTypeNav = () => (
    <Stack gap="xs" p="sm" h="100%" style={{ minHeight: 0 }}>
      {!isSearchMode && activeContext ? (
        <>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed">
            Разделы
          </Text>
          <ScrollArea
            style={{ flex: 1, minHeight: 0 }}
            offsetScrollbars
            type="auto"
          >
            <Stack gap={2}>
              <NavLink
                label="Все типы"
                description={articleCountLabel(activeContext.articles.length)}
                active={selectedTypeKey === "all"}
                onClick={() => {
                  onSelectType("all");
                  onSelectArticle(null);
                }}
                variant="subtle"
              />
              {materialTypes.map((mt) => {
                const n = typeCounts.get(mt.key) ?? 0;
                return (
                  <NavLink
                    key={mt.key}
                    label={mt.label}
                    description={n > 0 ? articleCountLabel(n) : "Пусто"}
                    rightSection={
                      canAdd ? (
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          aria-label="Добавить"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenCreate(
                              activeContext.project,
                              mt.key as SupportKnowledgeMaterialType,
                            );
                          }}
                        >
                          <IconPlus size={14} stroke={1.5} />
                        </ActionIcon>
                      ) : undefined
                    }
                    active={selectedTypeKey === mt.key}
                    onClick={() => {
                      onSelectType(mt.key);
                      onSelectArticle(null);
                    }}
                    variant="subtle"
                    opacity={n === 0 ? 0.65 : 1}
                  />
                );
              })}
              <NavLink
                label="Переменные"
                description={`${projectVariables.length}`}
                rightSection={
                  canAdd ? (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label="Добавить переменную"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenCreateVariable(activeContext.project?.id);
                      }}
                    >
                      <IconPlus size={14} stroke={1.5} />
                    </ActionIcon>
                  ) : undefined
                }
                active={selectedTypeKey === KNOWLEDGE_VARIABLES_SECTION}
                onClick={() => {
                  onSelectType(KNOWLEDGE_VARIABLES_SECTION);
                  onSelectArticle(null);
                }}
                variant="subtle"
              />
              <NavLink
                label="Метки"
                description={`${projectTags.length}`}
                rightSection={
                  canAdd ? (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label="Добавить метку"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenCreateTag(activeContext.project?.id);
                      }}
                    >
                      <IconPlus size={14} stroke={1.5} />
                    </ActionIcon>
                  ) : undefined
                }
                active={selectedTypeKey === KNOWLEDGE_TAGS_SECTION}
                onClick={() => {
                  onSelectType(KNOWLEDGE_TAGS_SECTION);
                  onSelectArticle(null);
                }}
                variant="subtle"
              />
              <NavLink
                label="Правила меток"
                description={`${projectTagRules.length}`}
                rightSection={
                  canAdd ? (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label="Добавить правило"
                      disabled={projectTags.length === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenCreateTagRule(activeContext.project?.id);
                      }}
                    >
                      <IconPlus size={14} stroke={1.5} />
                    </ActionIcon>
                  ) : undefined
                }
                active={selectedTypeKey === KNOWLEDGE_TAG_RULES_SECTION}
                onClick={() => {
                  onSelectType(KNOWLEDGE_TAG_RULES_SECTION);
                  onSelectArticle(null);
                }}
                variant="subtle"
              />
            </Stack>
          </ScrollArea>
        </>
      ) : (
        <Text size="sm" c="dimmed" p="xs">
          Выберите проект или используйте поиск
        </Text>
      )}
    </Stack>
  );

  const renderList = () => {
    if (!isSearchMode && isDictionarySection) return renderDictionaryList();

    return (
      <Stack gap="sm" p="sm" h="100%" style={{ minHeight: 0 }}>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Box style={{ minWidth: 0 }}>
            <Text fw={600} size="sm">
              {isSearchMode
                ? `Поиск: «${debouncedSearch}»`
                : isGeneralTab
                  ? "Общие"
                  : activeContext?.project?.name || "Материалы"}
            </Text>
            <Text size="xs" c="dimmed">
              {isSearchMode
                ? articleCountLabel(searchArticles.length)
                : articleCountLabel(browseArticles.length)}
            </Text>
          </Box>
          {!isSearchMode && activeContext && canAdd ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconPlus size={14} stroke={1.5} />}
              onClick={() =>
                onOpenCreate(
                  activeContext.project,
                  selectedTypeKey !== "all"
                    ? (selectedTypeKey as SupportKnowledgeMaterialType)
                    : undefined,
                )
              }
            >
              Добавить
            </Button>
          ) : null}
        </Group>

        {!isSearchMode ? (
          <TextInput
            size="xs"
            placeholder="Фильтр по названию в разделе…"
            leftSection={<IconSearch size={14} stroke={1.5} />}
            value={listFilter}
            onChange={(e) => setListFilter(e.currentTarget.value)}
          />
        ) : null}

        <ScrollArea
          style={{ flex: 1, minHeight: 0 }}
          offsetScrollbars
          type="auto"
        >
          {loading && listArticles.length === 0 ? (
            <PageCenterLoader />
          ) : listArticles.length === 0 ? (
            <EmptyState
              title={isSearchMode ? "Ничего не найдено" : "Нет материалов"}
              description={
                isSearchMode
                  ? "Попробуйте другой запрос"
                  : canAdd && activeContext
                    ? isGeneralTab
                      ? "Добавьте первый общий материал"
                      : "Добавьте первый материал в этот раздел"
                    : "В этом разделе пока пусто"
              }
              action={
                !isSearchMode && canAdd && activeContext ? (
                  <Button
                    size="compact-sm"
                    leftSection={<IconPlus size={14} stroke={1.5} />}
                    onClick={() =>
                      onOpenCreate(
                        activeContext.project,
                        selectedTypeKey !== "all"
                          ? (selectedTypeKey as SupportKnowledgeMaterialType)
                          : undefined,
                      )
                    }
                  >
                    Добавить материал
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Stack gap={4}>
              {listArticles.map((article) => {
                const project = article.project_id
                  ? projectById.get(article.project_id) ||
                    activeContext?.project ||
                    ({ id: article.project_id, name: "Проект" } as ProjectRow)
                  : null;
                const searchRow = searchResults?.find(
                  (r) => r.id === article.id,
                );
                const showProject = isGeneralTab || isSearchMode;
                const projectName = showProject
                  ? article.project_id
                    ? project?.name || project?.slug || "Проект"
                    : "Общие"
                  : null;
                return (
                  <ArticleRow
                    key={article.id}
                    article={article}
                    selected={article.id === selectedArticleId}
                    searchRow={searchRow}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    compact={isWide && !!viewArticle}
                    projectName={projectName}
                    onSelect={() => onSelectArticle(article)}
                    onEdit={() => onOpenEdit(project, article)}
                    onDelete={() => onDelete(project, article)}
                    onExport={() => onExportArticle(project, article)}
                    onImport={(file) => onImportArticle(project, article, file)}
                  />
                );
              })}
            </Stack>
          )}
        </ScrollArea>
      </Stack>
    );
  };

  const showInlineDetail = isWide && viewArticle && !isDictionarySection;

  return (
    <Box
      style={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: showInlineDetail
          ? isNavColumn && !isSearchMode && activeContext
            ? "220px minmax(280px, 360px) minmax(320px, 1fr)"
            : "minmax(280px, 360px) minmax(320px, 1fr)"
          : isNavColumn && !isSearchMode && activeContext
            ? "220px minmax(0, 1fr)"
            : "minmax(0, 1fr)",
        gap: theme.spacing.sm,
      }}
    >
      {isNavColumn && !isSearchMode && activeContext ? (
        <Box style={panelShell}>{renderTypeNav()}</Box>
      ) : null}

      <Box style={{ ...panelShell, minWidth: 0 }}>{renderList()}</Box>

      {showInlineDetail ? (
        <Box style={{ ...panelShell, padding: theme.spacing.sm, minWidth: 0 }}>
          <DetailPanel
            article={viewArticle}
            loading={viewArticleLoading}
            projectById={projectById}
            onExport={() =>
              onExportArticle(
                viewArticle.project_id
                  ? (projectById.get(viewArticle.project_id) ?? null)
                  : null,
                viewArticle,
              )
            }
            onImport={(file) =>
              onImportArticle(
                viewArticle.project_id
                  ? (projectById.get(viewArticle.project_id) ?? null)
                  : null,
                viewArticle,
                file,
              )
            }
            canEdit={canEdit}
            variableDefinitions={getVariableDefinitions?.(viewArticle)}
            relatedArticles={allKnownArticles}
            onArticleClick={(article) => {
              if (!isSearchMode) onSelectType(article.material_type || "all");
              onSelectArticle(article);
            }}
            onEdit={() => {
              const project = viewArticle.project_id
                ? (projectById.get(viewArticle.project_id) ?? null)
                : null;
              onOpenEdit(project, viewArticle);
            }}
            onClose={() => onSelectArticle(null)}
          />
        </Box>
      ) : null}
    </Box>
  );
}

export { DetailPanel };
